interface ReloadInfo {
  timestamp: number;
  count: number;
  lastUrl: string;
}

const RELOAD_DETECTION_KEY = 'app_reload_detection';
const AUTO_RELOAD_THRESHOLD_MS = 3000; // If reload happens within 3 seconds, likely automatic

export class ReloadDetector {
  private static instance: ReloadDetector;
  
  static getInstance(): ReloadDetector {
    if (!ReloadDetector.instance) {
      ReloadDetector.instance = new ReloadDetector();
    }
    return ReloadDetector.instance;
  }

  private getStoredInfo(): ReloadInfo | null {
    try {
      const stored = localStorage.getItem(RELOAD_DETECTION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error reading reload detection data:', error);
      return null;
    }
  }

  private storeInfo(info: ReloadInfo): void {
    try {
      localStorage.setItem(RELOAD_DETECTION_KEY, JSON.stringify(info));
    } catch (error) {
      console.error('Error storing reload detection data:', error);
    }
  }

  checkReloadType(): 'manual' | 'automatic' | 'first-load' {
    const now = Date.now();
    const currentUrl = window.location.href;
    const stored = this.getStoredInfo();

    if (!stored) {
      // First load ever
      this.storeInfo({
        timestamp: now,
        count: 1,
        lastUrl: currentUrl
      });
      return 'first-load';
    }

    const timeSinceLastLoad = now - stored.timestamp;
    const isAutoReload = timeSinceLastLoad < AUTO_RELOAD_THRESHOLD_MS;
    const isSameUrl = stored.lastUrl === currentUrl;

    // Update stored info
    this.storeInfo({
      timestamp: now,
      count: stored.count + 1,
      lastUrl: currentUrl
    });

    if (isAutoReload && isSameUrl) {
      return 'automatic';
    } else {
      return 'manual';
    }
  }

  getReloadStats(): { count: number; lastReloadTime: number; timeSinceLastReload: number } {
    const stored = this.getStoredInfo();
    if (!stored) {
      return { count: 0, lastReloadTime: 0, timeSinceLastReload: 0 };
    }

    return {
      count: stored.count,
      lastReloadTime: stored.timestamp,
      timeSinceLastReload: Date.now() - stored.timestamp
    };
  }

  clearReloadHistory(): void {
    localStorage.removeItem(RELOAD_DETECTION_KEY);
  }

  // Call this when a suspected automatic reload is detected
  logSuspiciousReload(context: string): void {
    const stats = this.getReloadStats();
    console.error(`🚨 SUSPICIOUS RELOAD DETECTED in ${context}:`, {
      reloadCount: stats.count,
      timeSinceLastReload: stats.timeSinceLastReload,
      currentUrl: window.location.href,
      timestamp: new Date().toISOString()
    });
  }
}

// Export convenience functions
export const checkReloadType = () => ReloadDetector.getInstance().checkReloadType();
export const getReloadStats = () => ReloadDetector.getInstance().getReloadStats();
export const clearReloadHistory = () => ReloadDetector.getInstance().clearReloadHistory();
export const logSuspiciousReload = (context: string) => ReloadDetector.getInstance().logSuspiciousReload(context);